import { useState } from "react";
import { mockBookingTimes, mockServices } from "../data/mock";
import { Calendar, Clock, User, Mail, Phone, MessageSquare, CheckCircle, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { format } from "date-fns";
import { toast } from "sonner";
import SectionLogo from "./SectionLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    date: null,
    time: "",
    message: ""
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields — list every empty one so the user knows
    // exactly what to fill in.
    const missing = [];
    if (!formData.name.trim()) missing.push("Full Name");
    if (!formData.email.trim()) missing.push("Email");
    if (!formData.phone.trim()) missing.push("Phone Number");
    if (!formData.service) missing.push("Service");

    if (missing.length > 0) {
      const label = missing.length === 1 ? "field" : "fields";
      toast.error(`Please complete the following ${label}: ${missing.join(", ")}`);
      return;
    }

    // Light email format check before hitting the API
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
    if (!emailOk) {
      toast.error("Please enter a valid email address (e.g. you@example.com)");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          date: formData.date ? format(formData.date, "PPP") : null,
          time: formData.time || null,
          message: formData.message || null
        })
      });

      if (!response.ok) {
        await response.text();
        toast.error("Booking may have been received. Please call 844-3ICEMAN to confirm.");
        setIsSubmitting(false);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setBookingId(data.booking_id);
        setIsSubmitted(true);
        toast.success("Booking submitted successfully! We'll contact you within 24 hours.");
      } else {
        toast.error(data.message || "Something went wrong. Please call 844-3ICEMAN to book.");
      }
    } catch {
      toast.error("Unable to connect. Please call 844-3ICEMAN to book your session.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewBooking = () => {
    setIsSubmitted(false);
    setBookingId(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      service: "",
      date: null,
      time: "",
      message: ""
    });
  };

  if (isSubmitted) {
    return (
      <section id="booking" className="py-24 bg-[#0a0318] relative overflow-hidden">
        <SectionLogo position="center" size="large" opacity={0.04} />
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-600 to-amber-400 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-3xl font-bold text-white mb-4">
              Booking Request Received!
            </h3>
            <p className="text-gray-400 mb-6">
              Thank you for choosing Ice Man Cryo! We'll contact you shortly to confirm your appointment.
            </p>
            {bookingId && (
              <p className="text-amber-300 text-sm mb-6">
                Booking Reference: <span className="font-mono font-bold">{bookingId.slice(0, 8)}</span>
              </p>
            )}
            <div className="bg-[#110422]/50 backdrop-blur-sm border border-amber-500/20 rounded-xl p-6 mb-6">
              <p className="text-sm text-gray-400 mb-2">We typically respond within</p>
              <p className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-amber-300 bg-clip-text text-transparent">
                24 Hours
              </p>
            </div>
            <Button 
              onClick={handleNewBooking}
              className="bg-gradient-to-r from-amber-600 to-amber-400 hover:from-amber-700 hover:to-amber-600"
            >
              Book Another Session
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-24 bg-[#0a0318] relative overflow-hidden">
      {/* Section Logo */}
      <SectionLogo position="center" size="large" opacity={0.04} />
      
      {/* Background Elements */}
      <div className="absolute top-20 right-10 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-amber-500/10 backdrop-blur-sm border border-amber-500/30 rounded-full px-4 py-2 mb-4">
            <span className="text-sm text-amber-300 font-medium">Get Started</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Book Your
            <span className="block bg-gradient-to-r from-amber-500 to-amber-300 bg-clip-text text-transparent">
              First Session
            </span>
          </h2>
          <p className="text-gray-400 text-lg">
            Fill out the form below and we'll contact you to confirm your appointment
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="bg-[#110422]/50 backdrop-blur-sm border border-amber-500/20 rounded-2xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white flex items-center gap-2">
                      <User className="w-4 h-4 text-amber-400" />
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="booking-name-input"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-[#110422]/50 border-amber-500/30 text-white placeholder:text-gray-500 focus:border-amber-400"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white flex items-center gap-2">
                      <Mail className="w-4 h-4 text-amber-400" />
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="booking-email-input"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-[#110422]/50 border-amber-500/30 text-white placeholder:text-gray-500 focus:border-amber-400"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white flex items-center gap-2">
                      <Phone className="w-4 h-4 text-amber-400" />
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="booking-phone-input"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-[#110422]/50 border-amber-500/30 text-white placeholder:text-gray-500 focus:border-amber-400"
                    />
                  </div>

                  {/* Service Selection */}
                  <div className="space-y-2">
                    <Label className="text-white">Service *</Label>
                    <Select 
                      value={formData.service} 
                      onValueChange={(value) => setFormData({...formData, service: value})}
                    >
                      <SelectTrigger 
                        data-testid="booking-service-select"
                        className="bg-[#110422]/50 border-amber-500/30 text-white"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0318] border-amber-500/30">
                        {mockServices.map((service) => (
                          <SelectItem 
                            key={service.id} 
                            value={service.title}
                            className="text-white hover:bg-amber-500/20"
                          >
                            {service.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Picker */}
                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-amber-400" />
                      Preferred Date
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="booking-date-picker"
                          className="w-full justify-start text-left font-normal bg-[#110422]/50 border-amber-500/30 text-white hover:bg-[#110422]/70"
                        >
                          {formData.date ? format(formData.date, "PPP") : <span className="text-gray-500">Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#0a0318] border-amber-500/30">
                        <CalendarComponent
                          mode="single"
                          selected={formData.date}
                          onSelect={(date) => setFormData({...formData, date})}
                          disabled={(date) => date < new Date()}
                          className="text-white"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Time Selection */}
                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-400" />
                      Preferred Time
                    </Label>
                    <Select 
                      value={formData.time} 
                      onValueChange={(value) => setFormData({...formData, time: value})}
                    >
                      <SelectTrigger 
                        data-testid="booking-time-select"
                        className="bg-[#110422]/50 border-amber-500/30 text-white"
                      >
                        <SelectValue placeholder="Select a time" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0318] border-amber-500/30">
                        {mockBookingTimes.map((time) => (
                          <SelectItem 
                            key={time} 
                            value={time}
                            className="text-white hover:bg-amber-500/20"
                          >
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2 mb-6">
                  <Label htmlFor="message" className="text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-amber-400" />
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="booking-message-input"
                    placeholder="Tell us about your goals or any specific areas you'd like to target..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="bg-[#110422]/50 border-amber-500/30 text-white placeholder:text-gray-500 focus:border-amber-400 min-h-[100px]"
                  />
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit"
                  data-testid="booking-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-amber-600 to-amber-400 hover:from-purple-700 hover:to-amber-600 text-white py-6 text-lg font-semibold disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Submitting...
                    </span>
                  ) : (
                    'Book Your Session'
                  )}
                </Button>

                <p className="text-center text-gray-500 text-sm mt-4">
                  * Required fields. We'll contact you within 24 hours to confirm.
                </p>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {/* Call Card */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-950 border-2 border-amber-500/30 hover:border-purple-500/60 rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/20">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-600 to-amber-400 flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Prefer to Call?</h3>
                  <a 
                    href="tel:8443423626"
                    className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-amber-300 bg-clip-text text-transparent hover:from-purple-300 hover:to-amber-300 transition-all block mb-1"
                  >
                    844-342-3626
                  </a>
                  <p className="text-amber-300 text-sm mb-4">or 844-3ICEMAN</p>
                  <a 
                    href="tel:8443423626"
                    data-testid="call-now-btn"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-400 hover:from-amber-700 hover:to-amber-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30"
                  >
                    <Phone className="w-4 h-4" />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Service Area Card */}
              <div className="bg-[#110422]/50 backdrop-blur-sm border border-amber-500/20 rounded-2xl p-6">
                <MapPin className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                <p className="text-gray-400 text-sm mb-1 text-center">Service Area</p>
                <p className="text-white font-semibold text-center text-lg">Midtown Atlanta</p>
                <p className="text-amber-300 text-sm text-center mt-2">Mobile Service - We Come to You!</p>
              </div>

              {/* Email Option */}
              <div className="text-center">
                <p className="text-gray-400 mb-2 text-sm">Prefer email?</p>
                <a 
                  href="mailto:info@icemancryo.com"
                  className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span className="font-semibold">info@icemancryo.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default BookingSection;
